import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';
import './Diagnostics.css';

interface Document {
  id: string;
  filename: string;
  ocr_text?: string;
  extracted_data?: any;
  created_at: string;
  status: string;
}

interface TestResult {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
}

export const Diagnostics: React.FC = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<string>('');
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateTest, setDateTest] = useState<any>(null);

  useEffect(() => {
    fetchDocuments();
    testDateConversion();
  }, []);

  const fetchDocuments = async () => {
    try {
      console.log('Fetching documents...');
      const response = await api.get('/api/v1/documents?limit=50');
      console.log('Documents response:', response.data);
      
      if (response.data && response.data.items) {
        setDocuments(response.data.items);
        console.log(`Loaded ${response.data.items.length} documents`);
      } else if (response.data && Array.isArray(response.data)) {
        // Handle if API returns array directly
        setDocuments(response.data);
        console.log(`Loaded ${response.data.length} documents (direct array)`);
      } else {
        console.warn('Unexpected response format:', response.data);
        setDocuments([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch documents:', error);
      addResult({
        success: false,
        message: 'Failed to load documents',
        error: error.response?.data?.detail || error.message
      });
    }
  };

  const testDateConversion = () => {
    const now = new Date();
    const tests = {
      'Current Date': now.toISOString(),
      'Date as Number': now.getTime(),
      'YYYYMMDD Format': `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`,
      'parseInt of Date': parseInt(now.toString()),
      'Number(Date)': Number(now),
      'Expected 20250729': '20250729'
    };
    setDateTest(tests);
  };

  const addResult = (result: TestResult) => {
    setTestResults((prev: TestResult[]) => [...prev, { ...result, timestamp: new Date().toISOString() }]);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const checkDocumentCount = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/document-test/count');
      addResult({
        success: true,
        message: 'Document Count Check',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Document Count Check Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const testOCRText = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/ocr-test/check/${selectedDoc}`);
      addResult({
        success: true,
        message: 'OCR Text Check',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'OCR Text Check Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const testAIExtraction = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/diagnostics/test-ai-extraction/${selectedDoc}`);
      addResult({
        success: true,
        message: 'AI Extraction Test',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'AI Extraction Test Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const testSimpleClaude = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/simple-test/test-claude/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Simple Claude API Test',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Simple Claude API Test Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const checkRawData = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/check-ai/raw-data/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Raw Document Data',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Raw Data Check Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const testVisionExtraction = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/api/v1/vision/extract/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Vision Extraction Test',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Vision Extraction Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const traceExtraction = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/extraction-debug/trace/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Extraction Trace Analysis',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Extraction Trace Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const testExtractionPipeline = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/api/v1/extraction-debug/test-extraction/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Extraction Pipeline Test',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Pipeline Test Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const fixAllDateAmounts = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/v1/extraction-debug/fix-all-date-amounts');
      addResult({
        success: true,
        message: 'Fixed All Date Amounts',
        data: response.data
      });
      // Refresh documents
      await fetchDocuments();
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Fix All Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const updateWithVision = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/api/v1/vision/update-with-vision/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Document Updated with Vision Data',
        data: response.data
      });
      // Refresh documents
      await fetchDocuments();
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Vision Update Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const retryWithLogging = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/api/v1/ai-debug/retry-with-logging/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Retry AI Extraction with Detailed Logging',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Retry with Logging Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const checkAmountDebug = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/debug-amount/check-amount/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Amount Field Debug Analysis',
        data: response.data
      });
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Amount Debug Check Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const fixAmountField = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/api/v1/debug-amount/fix-amount/${selectedDoc}`);
      addResult({
        success: true,
        message: 'Fix Amount Field Result',
        data: response.data
      });
      // Refresh documents list
      await fetchDocuments();
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Fix Amount Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  const findAmountBug = async () => {
    if (!selectedDoc) {
      addResult({ success: false, message: 'Please select a document first' });
      return;
    }
    
    const doc = documents.find(d => d.id === selectedDoc);
    if (!doc) return;

    addResult({
      success: true,
      message: 'Document Data Analysis',
      data: {
        'Document ID': doc.id,
        'Filename': doc.filename,
        'Has OCR Text': !!doc.ocr_text,
        'OCR Text Length': doc.ocr_text?.length || 0,
        'Has Extracted Data': !!doc.extracted_data,
        'Extracted Data': doc.extracted_data,
        'Extracted Amount': doc.extracted_data?.amount,
        'Amount Type': typeof doc.extracted_data?.amount,
        'Is 20250729?': doc.extracted_data?.amount === 20250729 || doc.extracted_data?.amount === '20250729'
      }
    });
  };

  const reprocessAllEmpty = async () => {
    setLoading(true);
    try {
      // Get all documents
      const response = await api.get('/api/v1/documents?limit=200');
      const documents = response.data.items || [];
      
      // Find documents with no amount
      const docsToReprocess = documents.filter((doc: any) => 
        doc.extracted_data && 
        (doc.extracted_data.amount === null || doc.extracted_data.amount === undefined)
      );
      
      if (docsToReprocess.length === 0) {
        addResult({
          success: true,
          message: 'No documents need reprocessing',
          data: { message: 'All documents already have amounts!' }
        });
        setLoading(false);
        return;
      }
      
      // Show warning with count
      const confirmed = window.confirm(
        `⚠️ WARNING: This will reprocess ${docsToReprocess.length} documents that have no amount extracted.\n\n` +
        `This process may take several minutes.\n\n` +
        `Are you sure you want to proceed?`
      );
      
      if (!confirmed) {
        setLoading(false);
        return;
      }
      
      addResult({
        success: true,
        message: `Starting reprocessing of ${docsToReprocess.length} documents...`,
        data: { count: docsToReprocess.length }
      });
      
      let success = 0;
      let failed = 0;
      
      // Process in batches of 5
      for (let i = 0; i < docsToReprocess.length; i += 5) {
        const batch = docsToReprocess.slice(i, i + 5);
        
        const promises = batch.map(async (doc: any) => {
          try {
            const response = await api.post(`/api/v1/documents/${doc.id}/reprocess`);
            if (response.status === 200) {
              success++;
              return true;
            } else {
              failed++;
              return false;
            }
          } catch (error) {
            failed++;
            return false;
          }
        });
        
        await Promise.all(promises);
        
        // Update progress
        addResult({
          success: true,
          message: `Progress: ${i + batch.length} of ${docsToReprocess.length} documents`,
          data: { processed: i + batch.length, total: docsToReprocess.length }
        });
        
        // Wait between batches
        if (i + 5 < docsToReprocess.length) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      addResult({
        success: true,
        message: 'Reprocessing Complete',
        data: {
          total: docsToReprocess.length,
          successful: success,
          failed: failed
        }
      });
      
    } catch (error: any) {
      addResult({
        success: false,
        message: 'Reprocessing Failed',
        error: error.response?.data?.detail || error.message
      });
    }
    setLoading(false);
  };

  return (
    <div className="diagnostics-page">
      <h1>AI Extraction Diagnostics</h1>
      
      {/* Emergency Reprocess Button - Prominent at top */}
      <div style={{
        backgroundColor: '#fff3cd',
        border: '2px solid #ffc107',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#856404', marginTop: 0 }}>Bulk Reprocessing Tool</h2>
        <p style={{ color: '#856404', marginBottom: '15px' }}>
          Use this to reprocess all documents that have no amount extracted.
          This is useful after fixing extraction issues.
        </p>
        <button 
          onClick={reprocessAllEmpty} 
          disabled={loading}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: '#ffc107',
            color: '#212529',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 'bold'
          }}
        >
          🔄 Reprocess All Documents with No Amount
        </button>
      </div>
      
      <div className="diagnostics-info">
        <h2>Known Issues:</h2>
        <ul>
          <li>Amount field showing "20250729" (today's date in YYYYMMDD format)</li>
          <li>AI not extracting vendor names, amounts, or dates</li>
          <li>Extracted data returning null or placeholder values</li>
        </ul>
      </div>

      <div className="date-test-section">
        <h2>Date Conversion Test (20250729 Bug)</h2>
        <pre>{JSON.stringify(dateTest, null, 2)}</pre>
      </div>

      <div className="document-selector">
        <h2>Select a Document to Test</h2>
        <div style={{ marginBottom: '10px' }}>
          <button onClick={fetchDocuments} disabled={loading}>
            Refresh Documents
          </button>
          <span style={{ marginLeft: '10px', color: '#666' }}>
            {documents.length} documents loaded
          </span>
        </div>
        <select 
          value={selectedDoc} 
          onChange={(e) => setSelectedDoc(e.target.value)}
          disabled={loading}
        >
          <option value="">-- Select a document --</option>
          {documents.map(doc => (
            <option key={doc.id} value={doc.id}>
              {doc.filename} - {doc.status} - {new Date(doc.created_at).toLocaleDateString()}
            </option>
          ))}
        </select>
      </div>

      <div className="test-buttons">
        <h2>Diagnostic Tests</h2>
        <button onClick={testOCRText} disabled={loading || !selectedDoc}>
          Test OCR Text Extraction
        </button>
        <button onClick={checkRawData} disabled={loading || !selectedDoc}>
          Check Raw Document Data
        </button>
        <button onClick={checkAmountDebug} disabled={loading || !selectedDoc}>
          Debug Amount Field (20250729)
        </button>
        <button onClick={fixAmountField} disabled={loading || !selectedDoc}>
          Fix Amount Field (Remove Date)
        </button>
        <button onClick={findAmountBug} disabled={loading || !selectedDoc}>
          Analyze 20250729 Bug
        </button>
        <button onClick={testAIExtraction} disabled={loading || !selectedDoc}>
          Test Full AI Extraction
        </button>
        <button onClick={testSimpleClaude} disabled={loading || !selectedDoc}>
          Test Simple Claude API
        </button>
        <button onClick={retryWithLogging} disabled={loading || !selectedDoc}>
          Retry with Detailed Logging
        </button>
        <button onClick={testVisionExtraction} disabled={loading || !selectedDoc}>
          Test Vision Extraction (Claude Vision)
        </button>
        <button onClick={updateWithVision} disabled={loading || !selectedDoc}>
          Update Document with Vision
        </button>
        <button onClick={traceExtraction} disabled={loading || !selectedDoc}>
          Trace Extraction Issue
        </button>
        <button onClick={testExtractionPipeline} disabled={loading || !selectedDoc}>
          Test Extraction Pipeline
        </button>
        <button onClick={fixAllDateAmounts} disabled={loading}>
          Fix ALL Date Amounts (Global)
        </button>
        <button onClick={clearResults} disabled={loading}>
          Clear Results
        </button>
        <button onClick={checkDocumentCount} disabled={loading}>
          Check Document Count
        </button>
        <button onClick={fetchDocuments} disabled={loading}>
          Reload Document List
        </button>
      </div>

      <div className="test-results">
        <h2>Test Results</h2>
        {testResults.length === 0 ? (
          <p>No tests run yet. Select a document and click a test button.</p>
        ) : (
          testResults.map((result, index) => (
            <div key={index} className={`result-item ${result.success ? 'success' : 'error'}`}>
              <h3>{result.message}</h3>
              {result.error && (
                <div className="error-message">
                  <strong>Error:</strong> {result.error}
                </div>
              )}
              {result.data && (
                <pre>{JSON.stringify(result.data, null, 2)}</pre>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};